/**
 * PersonalityCursorPanel — affichage des 13 axes de personnalité d'un
 * chauffeur, calculés à partir de ses `personalityTraits`.
 *
 * Phase F2 (04/02/2026) — répond à la demande utilisateur : "Il faudrait
 * une sorte de curseur sur les cases ci-dessous en exemple : longues
 * distances, types de contrats, l'export, formations, confort, électrique,
 * fidélité, salaire, stress, fatigue, ambiance équipe…".
 *
 * 10/02/2026 (Lot J3) — Étendu à 13 axes pour couvrir l'intégralité des
 * 14 dimensions du doc « Analyse catalogue flotte ». Permis CE laissé
 * en dehors (déjà géré par licenses[], catégorique).
 *
 * Le curseur est en lecture seule — il dérive des traits du chauffeur.
 * L'utilisateur peut ainsi rapidement comprendre pourquoi tel chauffeur
 * refuse l'export ou rechigne aux formations.
 */
import React from 'react';
import { Brain } from 'lucide-react';
import { PERSONALITY_AXES, derivePersonalityCursor } from '../../utils/driverPersonalityCursor';

const TICK_VALUES = [-2, -1, 0, 1, 2];

function colorForValue(v) {
  if (v <= -2) return 'bg-rose-500';
  if (v === -1) return 'bg-rose-400/70';
  if (v === 0) return 'bg-slate-500';
  if (v === 1) return 'bg-emerald-400/70';
  return 'bg-emerald-500';
}

function AxisRow({ axis, value }) {
  return (
    <div className="grid grid-cols-[100px_1fr_84px] items-center gap-2 text-[10px]" data-testid={`pcursor-axis-${axis.id}`}>
      <span className="text-slate-300 font-medium truncate" title={axis.label}>{axis.label}</span>
      <div className="relative h-2 rounded-full bg-ink-700/60 overflow-visible">
        {/* Center divider */}
        <div className="absolute left-1/2 top-0 w-px h-full bg-ink-500/60" />
        {/* Cursor */}
        {TICK_VALUES.map((tv) => {
          const isActive = tv === value;
          // Position 0% .. 100% mapped from -2..+2
          const pct = ((tv + 2) / 4) * 100;
          return (
            <span
              key={tv}
              className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-ink-900 ${
                isActive ? `w-3 h-3 ${colorForValue(value)} shadow` : 'w-1 h-1 bg-ink-500/40'
              }`}
              style={{ left: `${pct}%` }}
              aria-hidden="true"
            />
          );
        })}
      </div>
      <span className="flex items-center justify-between gap-1 text-[10px] leading-tight">
        <span className={value < 0 ? 'text-rose-300 font-semibold' : 'text-slate-500'}>{axis.pole[0]}</span>
        <span className="text-slate-700">·</span>
        <span className={value > 0 ? 'text-emerald-300 font-semibold' : 'text-slate-500'}>{axis.pole[1]}</span>
      </span>
    </div>
  );
}

export default function PersonalityCursorPanel({ driver }) {
  if (!driver) return null;
  const cursor = derivePersonalityCursor(driver);
  return (
    <div className="bg-ink-800/70 border border-ink-700 rounded-lg p-3 space-y-2" data-testid="personality-cursor-panel">
      <div className="flex items-center gap-1.5 pb-1 border-b border-ink-700/60">
        <Brain className="w-3.5 h-3.5 text-indigo-300" />
        <h4 className="text-xs uppercase tracking-wider font-bold text-indigo-300">Profil comportemental</h4>
      </div>
      <div className="space-y-1.5 pt-1">
        {PERSONALITY_AXES.map((axis) => (
          <AxisRow key={axis.id} axis={axis} value={cursor[axis.id] ?? 0} />
        ))}
      </div>
      <p className="text-[10px] text-slate-500 italic pt-1">
        Calculé depuis les traits ({driver.personalityTraits?.join(', ') || 'aucun'}).
      </p>
    </div>
  );
}
